import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EmptyReportSection from './EmptyReportSection';
import { numberOrZero } from './aiReportUtils';
import { reportStyles } from './styles';

const formatReportValue = (value) => {
  if (value === null || value === undefined || value === '') return 'N/A';
  return String(value);
};

const getFlowFailures = (flow) => (Array.isArray(flow.failures) ? flow.failures : []);

const getFailureFlowKey = (flow, index) =>
  `${flow.flow_id || 'flow'}-${flow.flow_name || 'unknown'}-${index}`;

export default function FailuresByFlowTable({ failuresByFlow = [] }) {
  return (
    <Paper variant="outlined" sx={reportStyles.reportPaperWithMargin}>
      <Accordion disableGutters elevation={0}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box>
            <Typography variant="subtitle1" sx={reportStyles.subsectionTitle}>
              Failures By Flow
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Flow-level failures with expandable attribute details.
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {failuresByFlow.length === 0 ? (
            <EmptyReportSection>No failures by flow reported.</EmptyReportSection>
          ) : (
            <Box>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '2fr 5fr 1fr auto',
                  gap: 2,
                  px: 2,
                  py: 1,
                }}
              >
                <Typography variant="body2" sx={reportStyles.tableHeaderCell}>
                  Flow
                </Typography>
                <Typography variant="body2" sx={reportStyles.tableHeaderCell}>
                  Test Case
                </Typography>
                <Typography variant="body2" sx={reportStyles.tableHeaderCell}>
                  Flow ID
                </Typography>
                <Typography variant="body2" align="right" sx={reportStyles.tableHeaderCell} pr={6}>
                  Failures
                </Typography>
              </Box>

              {failuresByFlow.map((flow, index) => {
                const flowKey = getFailureFlowKey(flow, index);
                const flowFailures = getFlowFailures(flow);
                const totalFlowFailures =
                  flow.total_failures !== undefined
                    ? numberOrZero(flow.total_failures)
                    : flowFailures.length;

                return (
                  <Accordion
                    key={flowKey}
                    disableGutters
                    elevation={0}
                    sx={{
                      mb: 1,
                      '&:before': {
                        display: 'none',
                      },
                    }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: '2fr 5fr 1fr auto',
                          alignItems: 'center',
                          gap: 2,
                          width: '100%',
                          pr: 1,
                        }}
                      >
                        <Typography variant="body2" sx={reportStyles.wordBreakCell}>
                          {formatReportValue(flow.flow_name)}
                        </Typography>
                        <Typography variant="body2" sx={reportStyles.wordBreakCell}>
                          {formatReportValue(flow.test_case)}
                        </Typography>
                        <Typography variant="body2" sx={reportStyles.wordBreakCell}>
                          {formatReportValue(flow.flow_id)}
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                          <Chip
                            label={`${totalFlowFailures} failures`}
                            size="small"
                            color={totalFlowFailures > 0 ? 'error' : 'success'}
                            variant="outlined"
                          />
                        </Box>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      {flowFailures.length === 0 ? (
                        <EmptyReportSection>
                          No failure details returned for this flow.
                        </EmptyReportSection>
                      ) : (
                        <TableContainer>
                          <Table size="small" aria-label={`${flow.flow_name} failures`}>
                            <TableHead>
                              <TableRow>
                                <TableCell sx={reportStyles.tableHeaderCell}>Event</TableCell>
                                <TableCell sx={reportStyles.tableHeaderCell}>Type</TableCell>
                                <TableCell sx={reportStyles.tableHeaderCell}>Attribute</TableCell>
                                <TableCell sx={reportStyles.tableHeaderCell}>Expected</TableCell>
                                <TableCell sx={reportStyles.tableHeaderCell}>mParticle</TableCell>
                                <TableCell sx={reportStyles.tableHeaderCell}>GMA</TableCell>
                                <TableCell sx={reportStyles.tableHeaderCell}>Source</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {flowFailures.map((failure, failureIndex) => (
                                <TableRow
                                  key={`${failure.event || 'event'}-${
                                    failure.variable_name || 'attribute'
                                  }-${failureIndex}`}
                                >
                                  <TableCell sx={reportStyles.wordBreakCell}>
                                    {formatReportValue(failure.event)}
                                  </TableCell>
                                  <TableCell sx={reportStyles.wordBreakCell}>
                                    {formatReportValue(failure.failure_type)}
                                  </TableCell>
                                  <TableCell sx={reportStyles.wordBreakCell}>
                                    {formatReportValue(failure.variable_name)}
                                  </TableCell>
                                  <TableCell sx={reportStyles.wordBreakCell}>
                                    {formatReportValue(failure.expected_value)}
                                  </TableCell>
                                  <TableCell sx={reportStyles.wordBreakCell}>
                                    {formatReportValue(failure.mparticle_actual)}
                                  </TableCell>
                                  <TableCell sx={reportStyles.wordBreakCell}>
                                    {formatReportValue(failure.gma_actual)}
                                  </TableCell>
                                  <TableCell sx={reportStyles.wordBreakCell}>
                                    {formatReportValue(failure.failing_source)}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Box>
          )}
        </AccordionDetails>
      </Accordion>
    </Paper>
  );
}
